import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Play } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Divider, Glyph, TattooBanner } from "@/components/Ornaments";
import Seo from "@/components/Seo";
import { useT, Rich } from "@/components/contentContext";

const API = process.env.REACT_APP_BACKEND_URL;

const AAA_GAMES = [
  {
    img: "/images/cover_crysis3.jpg",
    title: "Crysis 3",
    note: "Overgrown ruins and nanosuit heroics — a jungle of broken New York.",
  },
  {
    img: "/images/cover_armyoftwo.jpg",
    title: "Army of Two: Devil's Cartel",
    note: "Two guns, one contract — tactical brotherhood in a cartel war.",
  },
  {
    img: "/images/cover_nfs.jpg",
    title: "Need for Speed Most Wanted 2012",
    note: "Rain-slick streets, hot pistons and the city as an open throttle.",
  },
  {
    img: "/images/cover_fuse.jpg",
    title: "Fuse",
    note: "Covert ops meets a glowing alien energy that lights the night.",
  },
  {
    img: "/images/cover_moh.jpg",
    title: "Medal of Honor Warfighter",
    note: "Dust, steel and heartbeat — the quiet weight of special forces.",
  },
];

const INDIE_WORKS = [
  {
    img: "/images/indie_neonpunk.webp",
    title: "Neon Punk",
    desc: "Analog synth noir for the electric city — neon signage hum, rain on chrome, and a lone figure beneath the arcade light.",
    tags: ["Synthwave", "Noir", "Analog"],
  },
  {
    img: "/images/indie_aquapunk.webp",
    title: "Aqua Punk",
    desc: "Weightless strings and whale-song drones for the sunken metropolis — music that swims through flooded streets and glass tunnels.",
    tags: ["Ambient", "Aquatic", "Strings"],
  },
  {
    img: "/images/indie_gothpunk.webp",
    title: "Goth Punk",
    desc: "Darkwave choirs and bell-toll brass for candlelit spires — a gothic reverie scored beneath a watchful moon.",
    tags: ["Darkwave", "Gothic", "Choral"],
  },
  {
    img: "/images/indie_magipunk.webp",
    title: "Magi Punk",
    desc: "Celestial neoclassical for the arcane orrery — clockwork music boxes, planetary chimes and starlit wonder.",
    tags: ["Neoclassical", "Arcane", "Celestial"],
  },
];

const PosterCard = ({ game, index, sounding, onSelect }) => (
  <Reveal delay={0.07 * (index % 3)} className="relative flex justify-center">
    <div
      className={`poster-frame group w-[280px] cursor-pointer md:w-[300px] ${sounding ? "now-sounding" : ""}`}
      data-testid={`poster-frame-${index}`}
      onClick={() => onSelect(game)}
    >
      <div className="spotlight-cone" aria-hidden="true" />
      <div className="frame-glow" aria-hidden="true" />
      {sounding && (
        <p
          className="absolute left-1/2 top-3 z-10 -translate-x-1/2 border border-gold/60 bg-ink/85 px-3 py-1 font-jmono text-[8px] uppercase tracking-[0.3em] text-gold"
          data-testid={`poster-sounding-${index}`}
        >
          Now Sounding
        </p>
      )}
      <div className="relative overflow-hidden bg-[#0b0b11]">
        <img
          src={game.img}
          alt={`Official cover art for ${game.title}`}
          data-testid={`poster-img-${index}`}
          className="poster-img aspect-[4/5] w-full object-contain"
        />
      </div>
      <div className="relative mt-3 border border-line bg-[#0d0d13] px-4 py-3 text-center">
        <p className="font-display text-sm tracking-[0.12em] text-silver" data-testid={`poster-title-${index}`}>
          {game.title}
        </p>
        <p className="mt-1.5 font-jmono text-[9px] uppercase tracking-[0.3em] text-gold/80">
          Volt VMC × Electronic Arts
        </p>
        <p className="mt-2 font-jmono text-[8px] uppercase tracking-[0.3em] text-slate-600 transition-colors duration-500 group-hover:text-gold/70">
          Open the premiere file
        </p>
      </div>
    </div>
  </Reveal>
);

const Portfolio = () => {
  const t = useT();
  const [sounding, setSounding] = useState({ index: -1, playing: false });
  const [lightbox, setLightbox] = useState(null);
  const [videos, setVideos] = useState(null);
  const [videoOpen, setVideoOpen] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/videos`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error())))
      .then((d) => setVideos(d || []))
      .catch(() => setVideos([]));
  }, []);

  useEffect(() => {
    const onUpdate = (e) => setSounding(e.detail || { index: -1, playing: false });
    window.addEventListener("nox-now-playing", onUpdate);
    return () => window.removeEventListener("nox-now-playing", onUpdate);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setLightbox(null);
        setVideoOpen(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <main className="relative overflow-hidden">
      <Seo
        title="Portfolio — Gothic Game & TTRPG Scores | Nox Harmonia"
        description="AAA quality-assurance credits for Electronic Arts, indie game soundtracks and Curse of Strahd ambience — the gallery wall of composer Paul Elliott Messier."
      />
      <div className="starfield pointer-events-none absolute inset-x-0 top-0 h-[600px] opacity-60" aria-hidden="true" />

      {/* header */}
      <section className="relative z-10 mx-auto max-w-7xl px-6 pb-16 pt-40 md:pt-48 lg:px-10" data-testid="portfolio-header">
        <Reveal>
          <p className="eyebrow flex items-center gap-3">
            <Glyph className="h-3 w-3" />
            Portfolio
          </p>
          <h1 className="mt-8 font-display text-4xl leading-tight text-silver md:text-6xl">
            {t("portfolio.header.heading")}
          </h1>
          <Rich
            as="p"
            text={t("portfolio.header.body")}
            className="mt-6 max-w-2xl text-base font-light leading-relaxed text-slate-400 md:text-lg"
          />
        </Reveal>
      </section>

      {/* AAA gallery wall */}
      <section className="relative z-10 mx-auto max-w-7xl px-6 pb-24 md:pb-32 lg:px-10" data-testid="aaa-gallery">
        <div className="flex flex-wrap justify-center gap-x-10 gap-y-16 md:gap-x-12">
          {AAA_GAMES.map((g, i) => (
            <PosterCard
              key={g.title}
              game={g}
              index={i}
              sounding={sounding.index === i + 1 && sounding.playing}
              onSelect={setLightbox}
            />
          ))}
        </div>
        <Reveal delay={0.2}>
          <Divider className="mx-auto mt-24 max-w-md" />
          <p className="mx-auto mt-10 max-w-xl text-center font-cormorant text-lg italic text-slate-500">
            {t("portfolio.cta.line")}
          </p>
        </Reveal>
      </section>

      <TattooBanner className="my-2" />

      {/* indie soundtracks */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:py-32 lg:px-10" data-testid="indie-section">
        <Reveal>
          <p className="eyebrow">Beyond the Wall</p>
          <h2 className="mt-6 max-w-2xl font-display text-3xl leading-snug text-silver md:text-5xl">
            {t("portfolio.indie.heading")}
          </h2>
          <Rich
            as="p"
            text={t("portfolio.indie.body")}
            className="mt-6 max-w-2xl text-base font-light leading-relaxed text-slate-400 md:text-lg"
          />
        </Reveal>

        <div className="mt-16 grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {INDIE_WORKS.map((w, i) => (
            <Reveal key={w.title} delay={0.08 * i} className="h-full">
              <div className="flex h-full flex-col">
                <a
                  href="https://nox-harmonia.itch.io/"
                  target="_blank"
                  rel="noreferrer"
                  className="group block h-full border border-line bg-surface transition-all duration-500 hover:border-gold/50 hover:shadow-[0_0_50px_rgba(168,85,247,0.1)]"
                  data-testid={`indie-card-${i}`}
                >
                <div className="relative overflow-hidden">
                  <img
                    src={w.img}
                    alt={`Cover art for ${w.title}`}
                    data-testid={`indie-cover-${i}`}
                    className="aspect-square w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    style={{ filter: "brightness(0.92)" }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" aria-hidden="true" />
                </div>
                <div className="p-5">
                  <h3 className="font-display text-lg tracking-[0.1em] text-silver" data-testid={`indie-title-${i}`}>
                    {w.title}
                  </h3>
                  <p className="mt-3 min-h-[96px] text-sm font-light leading-relaxed text-slate-400">
                    {w.desc}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {w.tags.map((t) => (
                      <span
                        key={t}
                        className="border border-line px-2.5 py-1 font-jmono text-[9px] uppercase tracking-[0.25em] text-slate-500"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                  <div className="wf mt-6 opacity-60 transition-opacity duration-500 group-hover:opacity-100" aria-hidden="true">
                    {[0.9, 0.5, 1.1, 0.7, 1.3, 0.6, 1.0, 0.8, 1.2, 0.55, 0.95, 0.75].map((d, j) => (
                      <i key={j} style={{ animationDuration: `${d}s`, animationDelay: `${j * 0.08}s` }} />
                    ))}
                  </div>
                  <p className="mt-4 font-jmono text-[9px] uppercase tracking-[0.3em] text-gold/70">
                    Old samples · on itch.io
                  </p>
                </div>
              </a>
              <Link
                to={`/commission?mood=${encodeURIComponent(w.title)}`}
                className="mt-3 w-full cursor-pointer border border-line py-2.5 text-center font-jmono text-[9px] uppercase tracking-[0.3em] text-slate-400 transition-colors duration-300 hover:border-gold/50 hover:text-gold"
                data-testid={`indie-commission-${i}`}
              >
                Commission something like this
              </Link>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-14 text-center">
          <a
            href="https://nox-harmonia.itch.io/"
            target="_blank"
            rel="noreferrer"
            className="btn-night"
            data-testid="indie-itch-cta"
          >
            Visit the itch.io Vault
          </a>
          <motion.p className="mt-6 font-jmono text-[10px] uppercase tracking-[0.3em] text-slate-600">
            Old samples of these scores are taking their place on the vault
          </motion.p>
        </div>
      </section>

      {/* screening room — admin-curated YouTube videos */}
      {videos && videos.length > 0 && (
        <section className="relative z-10 mx-auto max-w-7xl px-6 pb-24 lg:px-10" data-testid="screenings-section">
          <Reveal>
            <p className="eyebrow">Screening Room</p>
            <h2 className="mt-6 max-w-2xl font-display text-3xl leading-snug text-silver md:text-5xl">
              {t("portfolio.screening.heading")}
            </h2>
            <Rich
              as="p"
              text={t("portfolio.screening.body")}
              className="mt-6 max-w-2xl text-base font-light leading-relaxed text-slate-400 md:text-lg"
            />
          </Reveal>

          <div className="mt-16 grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-3">
            {videos.map((v, i) => (
              <Reveal key={v.id} delay={0.08 * (i % 3)}>
                <button
                  className="group block w-full cursor-pointer border border-line bg-surface text-left transition-all duration-500 hover:border-gold/50 hover:shadow-[0_0_50px_rgba(168,85,247,0.1)]"
                  onClick={() => setVideoOpen(v)}
                  data-testid={`screening-card-${i}`}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={`https://i.ytimg.com/vi/${v.youtube_id}/hqdefault.jpg`}
                      alt={`Thumbnail for ${v.title}`}
                      data-testid={`screening-thumb-${i}`}
                      className="aspect-video w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                      style={{ filter: "brightness(0.88)" }}
                    />
                    <div
                      className="absolute inset-0 flex items-center justify-center bg-ink/30 transition-colors duration-500 group-hover:bg-ink/10"
                      aria-hidden="true"
                    >
                      <span className="flex h-14 w-14 items-center justify-center rounded-full border border-gold/70 bg-ink/80 text-gold backdrop-blur">
                        <Play className="ml-0.5 h-5 w-5" />
                      </span>
                    </div>
                  </div>
                  <div className="px-5 py-4">
                    <p className="font-display text-base tracking-[0.1em] text-silver" data-testid={`screening-title-${i}`}>
                      {v.title}
                    </p>
                    <p className="mt-2 font-jmono text-[9px] uppercase tracking-[0.3em] text-gold/70">
                      Screening · YouTube
                    </p>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="border-t border-line bg-[#090910] py-24" data-testid="portfolio-cta">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <Reveal>
            <h2 className="font-display text-3xl text-silver md:text-4xl">
              Your world deserves a score
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-base font-light text-slate-400">
              Build an estimate in a minute and open the conversation.
            </p>
            <Link to="/commission" data-testid="portfolio-cta-commission" className="btn-night mt-10">
              Commission Your Music
            </Link>
          </Reveal>
        </div>
      </section>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/92 p-6 backdrop-blur-xl"
            onClick={() => setLightbox(null)}
            data-testid="wall-lightbox"
          >
            <motion.div
              initial={{ opacity: 0, y: 32, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.97 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="relative grid w-full max-w-4xl grid-cols-1 items-center gap-10 border border-gold/30 bg-surface p-6 md:grid-cols-2 md:p-10"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="absolute right-4 top-4 cursor-pointer text-slate-500 transition-colors hover:text-silver"
                onClick={() => setLightbox(null)}
                data-testid="lightbox-close"
                aria-label="Close"
              >
                <X className="h-6 w-6" />
              </button>
              <div className="poster-frame !p-3" data-testid="lightbox-cover">
                <div className="spotlight-cone" aria-hidden="true" />
                <img
                  src={lightbox.img}
                  alt={`Cover art for ${lightbox.title}`}
                  className="aspect-[4/5] w-full bg-[#0b0b11] object-contain"
                />
              </div>
              <div>
                <p className="eyebrow">Premiere File</p>
                <h3 className="mt-5 font-display text-3xl leading-snug text-silver" data-testid="lightbox-title">
                  {lightbox.title}
                </h3>
                <p className="mt-6 font-jmono text-[10px] uppercase tracking-[0.3em] text-gold/80">
                  Quality Assurance &amp; Test Leadership — Volt VMC × Electronic Arts
                </p>
                <p className="mt-6 font-cormorant text-xl italic leading-relaxed text-slate-300" data-testid="lightbox-story">
                  {lightbox.note}
                </p>
                <Divider className="my-8" />
                <p className="text-sm font-light leading-relaxed text-slate-500">
                  My work on this title: tests run and documented, glitches categorised and
                  verified, findings reported to higher management, physical builds produced,
                  machinery updated — and a 20-tester team managed to ship.
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {videoOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/92 p-4 backdrop-blur-xl"
            onClick={() => setVideoOpen(null)}
            data-testid="video-lightbox"
          >
            <motion.div
              initial={{ opacity: 0, y: 32, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.97 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-4xl border border-gold/30 bg-surface p-3 md:p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="absolute -top-12 right-0 cursor-pointer text-slate-400 transition-colors hover:text-silver"
                onClick={() => setVideoOpen(null)}
                data-testid="video-lightbox-close"
                aria-label="Close"
              >
                <X className="h-6 w-6" />
              </button>
              <div className="aspect-video w-full bg-black" data-testid="video-lightbox-frame">
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${videoOpen.youtube_id}?autoplay=1&rel=0`}
                  title={videoOpen.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="h-full w-full"
                />
              </div>
              <p
                className="px-2 py-3 font-display text-base tracking-[0.1em] text-silver"
                data-testid="video-lightbox-title"
              >
                {videoOpen.title}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
};

export default Portfolio;
